import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { fmtDate } from "../../lib/status";

const ROLES = [
    "admin",
    "manager",
    "customer_support",
    "finance",
    "operations",
    "technician",
    "customer",
];

const STATUS_OPTIONS = [
    { value: "", label: "All statuses" },
    { value: "active", label: "Active" },
    { value: "blocked", label: "Blocked" },
    { value: "deleted", label: "Deleted" },
];

function Modal({ open, onClose, title, children, testid }) {
    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            data-testid={testid}
        >
            <div className="bg-[#180A21] border border-[#2B1438] w-full max-w-lg max-h-[90vh] overflow-y-auto">
                <div className="px-6 py-4 border-b border-[#2B1438] flex items-center justify-between">
                    <div className="font-serif text-xl text-white">{title}</div>
                    <button
                        className="text-white/60 hover:text-white"
                        onClick={onClose}
                        data-testid={`${testid}-close`}
                    >
                        ✕
                    </button>
                </div>
                <div className="p-6">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="lx-label text-white/60 mb-1.5 block">{label}</span>
            {children}
        </label>
    );
}

const inputCls =
    "w-full px-3 py-2 bg-[#0F0614] border border-[#2B1438] text-white text-sm focus:border-[#D4AF37] focus:outline-none";

export default function AdminUsers() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [roleFilter, setRoleFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [q, setQ] = useState("");

    // create modal
    const [createOpen, setCreateOpen] = useState(false);
    const [newUser, setNewUser] = useState({
        email: "",
        name: "",
        role: "operations",
        phone: "",
    });
    const [tempPw, setTempPw] = useState(null);

    // edit modal
    const [editTarget, setEditTarget] = useState(null);

    // block modal
    const [blockTarget, setBlockTarget] = useState(null);
    const [blockReason, setBlockReason] = useState("");

    // reset password modal
    const [resetTarget, setResetTarget] = useState(null);
    const [resetTempPw, setResetTempPw] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const params = {};
            if (roleFilter) params.role = roleFilter;
            if (statusFilter) params.status = statusFilter;
            if (q) params.q = q;
            const { data } = await api.get("/users", { params });
            setUsers(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const params = {};
                if (roleFilter) params.role = roleFilter;
                if (statusFilter) params.status = statusFilter;
                if (q) params.q = q;
                const { data } = await api.get("/users", { params });
                if (!cancelled) setUsers(data);
            } catch (e) {
                if (!cancelled)
                    toast.error(formatApiError(e.response?.data?.detail));
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [roleFilter, statusFilter, q]);

    const createUser = async () => {
        try {
            const { data } = await api.post("/users", newUser);
            setTempPw(data.temp_password);
            toast.success("User created");
            await load();
            setNewUser({ email: "", name: "", role: "operations", phone: "" });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const saveEdit = async () => {
        try {
            const payload = {
                name: editTarget.name,
                phone: editTarget.phone,
                role: editTarget.role,
                base_city: editTarget.base_city,
            };
            await api.patch(`/users/${editTarget.id}`, payload);
            toast.success("User updated");
            setEditTarget(null);
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const blockUser = async () => {
        try {
            await api.post(`/users/${blockTarget.id}/block`, {
                reason: blockReason || null,
            });
            toast.success("User blocked");
            setBlockTarget(null);
            setBlockReason("");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const unblockUser = async (u) => {
        try {
            await api.post(`/users/${u.id}/unblock`);
            toast.success("User unblocked");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const resetPassword = async () => {
        try {
            const { data } = await api.post(
                `/users/${resetTarget.id}/reset-password`,
                {},
            );
            setResetTempPw(data.temp_password);
            toast.success("Temporary password issued");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const deleteUser = async (u) => {
        if (!window.confirm(`Soft-delete user ${u.email}?`)) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success("User deleted");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const restoreUser = async (u) => {
        try {
            await api.post(`/users/${u.id}/restore`);
            toast.success("User restored");
            await load();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        }
    };

    const filtered = useMemo(() => {
        const term = q.trim().toLowerCase();
        if (!term) return users;
        return users.filter(
            (u) =>
                (u.email || "").toLowerCase().includes(term) ||
                (u.name || "").toLowerCase().includes(term) ||
                (u.phone || "").toLowerCase().includes(term),
        );
    }, [users, q]);

    return (
        <div className="space-y-6">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="lx-label text-[#D4AF37] mb-2">Identity</div>
                    <h1 className="font-serif text-4xl text-white">Users & Access</h1>
                    <p className="text-white/60 text-sm mt-2">
                        Create staff, technicians, customers. Block, reset, and audit.
                    </p>
                </div>
                <button
                    data-testid="users-create-btn"
                    className="lx-btn-primary px-5 py-2.5"
                    onClick={() => {
                        setTempPw(null);
                        setCreateOpen(true);
                    }}
                >
                    + Add user
                </button>
            </div>

            {/* Filters */}
            <div className="lx-card-dark p-4 flex gap-3 flex-wrap items-center">
                <input
                    data-testid="users-search-input"
                    className={inputCls + " max-w-xs"}
                    placeholder="Search name, email, phone…"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                />
                <select
                    data-testid="users-role-filter"
                    className={inputCls + " max-w-xs"}
                    value={roleFilter}
                    onChange={(e) => setRoleFilter(e.target.value)}
                >
                    <option value="">All roles</option>
                    {ROLES.map((r) => (
                        <option key={r} value={r}>
                            {r}
                        </option>
                    ))}
                </select>
                <select
                    data-testid="users-status-filter"
                    className={inputCls + " max-w-xs"}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value}>
                            {s.label}
                        </option>
                    ))}
                </select>
                <button
                    data-testid="users-refresh-btn"
                    onClick={load}
                    className="text-xs text-[#D4AF37] hover:underline"
                >
                    Refresh
                </button>
            </div>

            {/* Table */}
            <div className="lx-card-dark overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-left text-white/60 border-b border-[#2B1438]">
                        <tr>
                            <th className="p-4 lx-label">Name</th>
                            <th className="p-4 lx-label">Email</th>
                            <th className="p-4 lx-label">Role</th>
                            <th className="p-4 lx-label">Status</th>
                            <th className="p-4 lx-label">Joined</th>
                            <th className="p-4 lx-label text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.map((u) => {
                            const blocked = !!u.blocked_at;
                            const deleted = !!u.deleted_at;
                            return (
                                <tr
                                    key={u.id}
                                    className="border-b border-[#2B1438]"
                                    data-testid={`user-row-${u.id}`}
                                >
                                    <td className="p-4 text-white">{u.name}</td>
                                    <td className="p-4 text-white/80 break-all">{u.email}</td>
                                    <td className="p-4">
                                        <span className="lx-label px-2 py-1 border border-[#D4AF37]/30 text-[#D4AF37]">
                                            {u.canonical_role || u.role}
                                        </span>
                                    </td>
                                    <td className="p-4">
                                        {deleted ? (
                                            <span className="text-xs text-red-400">Deleted</span>
                                        ) : blocked ? (
                                            <span className="text-xs text-yellow-400">Blocked</span>
                                        ) : (
                                            <span className="text-xs text-green-400">Active</span>
                                        )}
                                    </td>
                                    <td className="p-4 text-white/60">
                                        {fmtDate(u.created_at)}
                                    </td>
                                    <td className="p-4 text-right space-x-2">
                                        {deleted ? (
                                            <button
                                                data-testid={`user-restore-${u.id}`}
                                                onClick={() => restoreUser(u)}
                                                className="text-xs text-green-400 hover:underline"
                                            >
                                                Restore
                                            </button>
                                        ) : (
                                            <>
                                                <button
                                                    data-testid={`user-edit-${u.id}`}
                                                    onClick={() => setEditTarget({ ...u })}
                                                    className="text-xs text-[#D4AF37] hover:underline"
                                                >
                                                    Edit
                                                </button>
                                                <button
                                                    data-testid={`user-reset-${u.id}`}
                                                    onClick={() => {
                                                        setResetTempPw(null);
                                                        setResetTarget(u);
                                                    }}
                                                    className="text-xs text-white/60 hover:text-white"
                                                >
                                                    Reset PW
                                                </button>
                                                {blocked ? (
                                                    <button
                                                        data-testid={`user-unblock-${u.id}`}
                                                        onClick={() => unblockUser(u)}
                                                        className="text-xs text-yellow-400 hover:underline"
                                                    >
                                                        Unblock
                                                    </button>
                                                ) : (
                                                    <button
                                                        data-testid={`user-block-${u.id}`}
                                                        onClick={() => setBlockTarget(u)}
                                                        className="text-xs text-yellow-400 hover:underline"
                                                    >
                                                        Block
                                                    </button>
                                                )}
                                                <button
                                                    data-testid={`user-delete-${u.id}`}
                                                    onClick={() => deleteUser(u)}
                                                    className="text-xs text-red-400 hover:underline"
                                                >
                                                    Delete
                                                </button>
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {!loading && filtered.length === 0 && (
                    <div className="p-12 text-center text-white/60">No users found.</div>
                )}
                {loading && (
                    <div className="p-12 text-center text-white/60">Loading…</div>
                )}
            </div>

            {/* Create modal */}
            <Modal
                open={createOpen}
                onClose={() => {
                    setCreateOpen(false);
                    setTempPw(null);
                }}
                title="Add user"
                testid="user-create-modal"
            >
                {tempPw ? (
                    <div className="space-y-4">
                        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4">
                            <div className="lx-label text-[#D4AF37] mb-2">
                                Temporary password — copy now
                            </div>
                            <div className="font-mono text-xl text-white break-all" data-testid="temp-password-value">
                                {tempPw}
                            </div>
                            <div className="text-xs text-white/60 mt-2">
                                The user will be forced to change this on first login.
                            </div>
                        </div>
                        <button
                            data-testid="temp-password-done"
                            className="lx-btn-primary w-full py-2.5"
                            onClick={() => {
                                setCreateOpen(false);
                                setTempPw(null);
                            }}
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <Field label="Name">
                            <input
                                data-testid="create-user-name"
                                className={inputCls}
                                value={newUser.name}
                                onChange={(e) =>
                                    setNewUser({ ...newUser, name: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Email">
                            <input
                                data-testid="create-user-email"
                                type="email"
                                className={inputCls}
                                value={newUser.email}
                                onChange={(e) =>
                                    setNewUser({ ...newUser, email: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Phone (optional)">
                            <input
                                data-testid="create-user-phone"
                                className={inputCls}
                                value={newUser.phone}
                                onChange={(e) =>
                                    setNewUser({ ...newUser, phone: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Role">
                            <select
                                data-testid="create-user-role"
                                className={inputCls}
                                value={newUser.role}
                                onChange={(e) =>
                                    setNewUser({ ...newUser, role: e.target.value })
                                }
                            >
                                {ROLES.map((r) => (
                                    <option key={r} value={r}>
                                        {r}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <p className="text-xs text-white/60">
                            A secure temporary password will be generated. The user must
                            change it on first login.
                        </p>
                        <button
                            data-testid="create-user-submit"
                            className="lx-btn-primary w-full py-2.5"
                            onClick={createUser}
                        >
                            Create user
                        </button>
                    </div>
                )}
            </Modal>

            {/* Edit modal */}
            <Modal
                open={!!editTarget}
                onClose={() => setEditTarget(null)}
                title={`Edit — ${editTarget?.email || ""}`}
                testid="user-edit-modal"
            >
                {editTarget && (
                    <div className="space-y-4">
                        <Field label="Name">
                            <input
                                data-testid="edit-user-name"
                                className={inputCls}
                                value={editTarget.name || ""}
                                onChange={(e) =>
                                    setEditTarget({ ...editTarget, name: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Phone">
                            <input
                                data-testid="edit-user-phone"
                                className={inputCls}
                                value={editTarget.phone || ""}
                                onChange={(e) =>
                                    setEditTarget({ ...editTarget, phone: e.target.value })
                                }
                            />
                        </Field>
                        <Field label="Role">
                            <select
                                data-testid="edit-user-role"
                                className={inputCls}
                                value={editTarget.role || ""}
                                onChange={(e) =>
                                    setEditTarget({ ...editTarget, role: e.target.value })
                                }
                            >
                                {ROLES.map((r) => (
                                    <option key={r} value={r}>
                                        {r}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Base city">
                            <input
                                data-testid="edit-user-city"
                                className={inputCls}
                                value={editTarget.base_city || ""}
                                onChange={(e) =>
                                    setEditTarget({ ...editTarget, base_city: e.target.value })
                                }
                            />
                        </Field>
                        <button
                            data-testid="edit-user-save"
                            className="lx-btn-primary w-full py-2.5"
                            onClick={saveEdit}
                        >
                            Save changes
                        </button>
                    </div>
                )}
            </Modal>

            {/* Block modal */}
            <Modal
                open={!!blockTarget}
                onClose={() => setBlockTarget(null)}
                title={`Block — ${blockTarget?.email || ""}`}
                testid="user-block-modal"
            >
                <div className="space-y-4">
                    <Field label="Reason (optional)">
                        <textarea
                            data-testid="block-reason"
                            className={inputCls}
                            rows={3}
                            value={blockReason}
                            onChange={(e) => setBlockReason(e.target.value)}
                        />
                    </Field>
                    <button
                        data-testid="block-submit"
                        className="w-full py-2.5 bg-yellow-500 text-black font-medium hover:bg-yellow-400"
                        onClick={blockUser}
                    >
                        Block user
                    </button>
                </div>
            </Modal>

            {/* Reset password modal */}
            <Modal
                open={!!resetTarget}
                onClose={() => {
                    setResetTarget(null);
                    setResetTempPw(null);
                }}
                title={`Reset password — ${resetTarget?.email || ""}`}
                testid="user-reset-modal"
            >
                {resetTempPw ? (
                    <div className="space-y-4">
                        <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-4">
                            <div className="lx-label text-[#D4AF37] mb-2">
                                Temporary password — copy now
                            </div>
                            <div
                                className="font-mono text-xl text-white break-all"
                                data-testid="reset-temp-password-value"
                            >
                                {resetTempPw}
                            </div>
                            <div className="text-xs text-white/60 mt-2">
                                User must change it on next login.
                            </div>
                        </div>
                        <button
                            data-testid="reset-done"
                            className="lx-btn-primary w-full py-2.5"
                            onClick={() => {
                                setResetTarget(null);
                                setResetTempPw(null);
                            }}
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <div className="space-y-4">
                        <p className="text-sm text-white/80">
                            A secure temporary password will be generated. The user will be
                            forced to change it on next login.
                        </p>
                        <button
                            data-testid="reset-submit"
                            className="lx-btn-primary w-full py-2.5"
                            onClick={resetPassword}
                        >
                            Generate temporary password
                        </button>
                    </div>
                )}
            </Modal>
        </div>
    );
}
